import React from 'react';

/* v8 Path T — shared DRY in-cell sparkline segments.
   One SVG drawn behind each amount cell, connecting the previous period's
   value to the current period's value. Stitched together across a row they
   form a continuous sparkline underlay that sits BEHIND the numbers.

   DRY contract — both `<FormalStatementTable/>` and `<MetricHorizonTable/>`
   now consume this module so the 3-statement tables and Comparative Tables
   render identical in-cell trends. The two visual controls are:
     1. `gradient` — toggles the area-fill wedge beneath the line
        (wires to `scenario.sparklineGradient`). Off ⇒ faint line only.
     2. `enabled` — killswitch for rendering at all (per-section feature
        gate in consumer tables).

   Perf notes (preserved from original in FormalStatementTable):
   - <defs> hoisted ONCE per table via `<SparkDefs/>`; each segment
     references them by `url(#<id>-up|down)`. Avoids re-parsing hundreds
     of `<defs>` per scroll frame.
   - Each segment is `pointer-events-none` + `z-[1]` so it never eats
     clicks and sits behind the text. */

export function RowSparkSegment({ prev, curr, min, max, rising, tone, gradient, gradUpId, gradDownId, gradNeutralId }) {
  if (typeof prev !== 'number' || !Number.isFinite(prev)) return null;
  if (typeof curr !== 'number' || !Number.isFinite(curr)) return null;
  /* v8 Path AE — removed the absolute `max - min < 1` gate. It was
     currency-shaped and suppressed segments for ratio/pct rows where
     the whole horizon lives in a sub-unit band (e.g. Debt-to-Assets
     0.966→1.017). The caller already gates with format-aware
     `isFlatSeries` / `seriesRange(format)`; if we got called with a
     non-flat min/max, we trust it and draw. The `|| 1` guard below
     still prevents divide-by-zero if min==max slipped through. */
  const range = max - min || 1;
  const y1 = 90 - ((prev - min) / range) * 80;
  const y2 = 90 - ((curr - min) / range) * 80;
  /* v8 Path AI — color selection, tone-first.
     - `tone: 'good'` → green (favorable direction for this metric)
     - `tone: 'bad'`  → red  (unfavorable direction)
     - `tone: 'neutral'` → gray-blue (metric has no preferred direction,
       e.g. DPO/AP turnover where the explanation is context-dependent)
     - `tone` undefined → fall back to `rising` (legacy: up=green). */
  const favorable = tone
    ? tone === 'good'
    : rising;
  const isNeutral = tone === 'neutral';
  const hex = isNeutral ? '#8b93a7' : (favorable ? '#4ade80' : '#f87171');
  const lineOpacity = gradient ? 0.55 : 0.13;
  /* Net-flat / noise-dominated rows get a GREY gradient fill to match
     their grey line. Caller passes `gradNeutralId` explicitly so the
     neutral def works regardless of how the up/down IDs were composed
     (prefix vs suffix convention across tables). */
  const fillId = isNeutral
    ? gradNeutralId
    : (favorable ? gradUpId : gradDownId);
  return (
    <svg
      className="absolute top-0 left-0 w-full h-full pointer-events-none z-[1]"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {gradient && (
        <polygon
          points={`0,${y1} 100,${y2} 100,100 0,100`}
          fill={`url(#${fillId})`}
        />
      )}
      <line x1="0" y1={y1} x2="100" y2={y2} stroke={hex} strokeOpacity={lineOpacity} strokeWidth="6" strokeLinecap="round" />
    </svg>
  );
}

/* One hidden SVG with the shared up/down/neutral gradient defs. Render
   once per table instance with unique IDs so multiple tables on the
   same page (e.g. FND's three-statement trio) don't collide.

   `neutralId` is required when net-flat / noise-dominated rows should
   render a grey fill (which is app-wide now). Callers compose their
   three IDs the same way they compose up/down. */
export function SparkDefs({ upId, downId, neutralId }) {
  return (
    <svg width="0" height="0" className="absolute" aria-hidden="true">
      <defs>
        <linearGradient id={upId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#4ade80" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#4ade80" stopOpacity="0" />
        </linearGradient>
        <linearGradient id={downId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#f87171" stopOpacity="0.28" />
          <stop offset="100%" stopColor="#f87171" stopOpacity="0" />
        </linearGradient>
        {neutralId && (
          /* Neutral fill for net-flat / noise-dominated rows — matches
             the grey line color (#8b93a7 from TONE_STROKE.neutral). */
          <linearGradient id={neutralId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#8b93a7" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#8b93a7" stopOpacity="0" />
          </linearGradient>
        )}
      </defs>
    </svg>
  );
}

/* Helper: compute a stable per-table prefix so two tables rendered on
   the same page don't collide on gradient IDs. */
export function makeSparkIds(prefix) {
  return {
    upId:      `${prefix}-spark-up`,
    downId:    `${prefix}-spark-down`,
    neutralId: `${prefix}-spark-neutral`,
  };
}
